import { inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { LocationFactory } from 'app/factories';
import { ApolloQueryResult } from 'app/graphql/apollo-query-result';
import {
    Coordinate_Input,
    Countries_Query_Order_By,
    CountriesQuery,
    DirectionTypeEnum,
} from 'app/graphql/generated/graphql-types';
import { GenericOrderBy, PositionErrorsEnum } from 'app/interfaces';

@Injectable()
export class LocationService {
    private locationFactory = inject(LocationFactory);

    private geolocationObservable: Observable<GeolocationPosition>;
    private userLocation = new ReplaySubject<GeolocationPosition>(1);
    private userQueried = false;

    constructor() {
        this.createGeolocationObservable();
    }

    getCountries(
        activeOnly?: boolean,
        sort: GenericOrderBy<DirectionTypeEnum, Countries_Query_Order_By> = {
            orderDirection: DirectionTypeEnum.ASC,
            orderBy: Countries_Query_Order_By.active,
        },
    ): Observable<ApolloQueryResult<CountriesQuery>> {
        return this.locationFactory.getCountries({
            activeOnly,
            orderBy: sort.orderBy,
            orderDirection: sort.orderDirection,
        });
    }

    getCurrentLocation(): Observable<Coordinate_Input> {
        return this.geolocationObservable.pipe(
            first(),
            map((position) => {
                return {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                };
            }),
        );
    }

    private createGeolocationObservable(): void {
        this.geolocationObservable = new Observable((observer) => {
            this.queryUserForLocation();

            this.userLocation.subscribe(
                (userLocation) => {
                    observer.next(userLocation);
                    observer.complete();
                },
                (error) => observer.error(error),
                () => observer.complete(),
            );
        });
    }

    private queryUserForLocation(): void {
        if (this.userQueried) {
            return;
        }

        this.userQueried = true;

        if (window.navigator && window.navigator.geolocation) {
            window.navigator.geolocation.getCurrentPosition(
                (position) => {
                    this.userLocation.next(position);
                    this.userLocation.complete();
                },
                (error) => {
                    this.userLocation.error(error);
                },
                { timeout: 5000 },
            );
        } else {
            this.userLocation.error({
                code: PositionErrorsEnum.UnsupportedBrowser,
            });
        }
    }
}
